/* eslint-disable react/prop-types */
import React, { useRef, useState } from 'react';
import { Col, Row } from 'antd';
import { Carousel } from 'antd';
import { FacilityDetailType } from '../../../types/types';
import {
  CalendarOutlined,
  EnvironmentOutlined,
  MailOutlined,
  PhoneOutlined,
  TrophyOutlined,
  UserOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import { COLORS, FONT_WEIGHTS, TEXT_SIZES } from '../../../utils/constants';
import moment from 'moment';
import { AmenityEnum } from '../../../enums/enums';
import NoImg from '../../../assets/no-image.png';

interface OverviewSectionProps {
  facilityData: FacilityDetailType;
}

interface SimpleCarouselRef {
  next: () => void;
  prev: () => void;
}

const OverviewSection: React.FC<OverviewSectionProps> = ({ facilityData }) => {
  const carouselRef = useRef<SimpleCarouselRef | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  const attachments = facilityData?.attachments || [];
  const totalSlides = attachments.length;
  const next = () => carouselRef.current?.next();
  const prev = () => carouselRef.current?.prev();

  const contactInfo = [
    {
      title: 'Address',
      icon: (
        <IconWrapper>
          <EnvironmentOutlined />
        </IconWrapper>
      ),
      value: `${facilityData?.facilityAddress}, ${facilityData?.city}, ${facilityData?.state}, ${facilityData?.country}, ${facilityData?.zipCode}`,
    },
    {
      title: 'Email',
      icon: (
        <IconWrapper>
          <MailOutlined />
        </IconWrapper>
      ),
      value: facilityData?.facilityContactEmail,
    },
    {
      title: 'Phone',
      icon: (
        <IconWrapper>
          <PhoneOutlined rotate={90} />
        </IconWrapper>
      ),
      value: facilityData?.facilityContactPhone,
    },
    {
      title: 'Contact Person',
      icon: (
        <IconWrapper>
          <UserOutlined />
        </IconWrapper>
      ),
      value: facilityData?.facilityContactName,
    },
  ];

  return (
    <Row gutter={[16, 16]}>
      <CardsWrapper span={6}>
        <OverviewWrapper>
          <CarouselWrapper>
            {totalSlides > 1 && currentSlide > 0 && (
              <ArrowButton position="left" onClick={prev} aria-label="previous">
                <LeftOutlined />
              </ArrowButton>
            )}
            {totalSlides > 0 ? (
              <StyledCarousel
                ref={carouselRef as unknown as React.RefObject<any>}
                arrows={false}
                infinite={false}
                beforeChange={(_, nextSlide) => setCurrentSlide(nextSlide)}
              >
                {attachments.map((item, index) => (
                  <div key={index}>
                    <Image
                      src={item.attachmentUrl ?? NoImg}
                      alt={`Attachment ${index + 1}`}
                    />
                  </div>
                ))}
              </StyledCarousel>
            ) : (
              <NoImagesWrapper>No Images Added</NoImagesWrapper>
            )}
            {totalSlides > 1 && currentSlide < totalSlides - 1 && (
              <ArrowButton position="right" onClick={next} aria-label="next">
                <RightOutlined />
              </ArrowButton>
            )}
          </CarouselWrapper>
          <DescriptionWrapper>
            <Title>Facility Overview</Title>
            <GrayText>{facilityData?.facilityDescription}</GrayText>
          </DescriptionWrapper>
        </OverviewWrapper>
        <Card>
          <Title>Quick Stats</Title>
          <TitleAndValueWrapper>
            <TitleAndCountWrapper>
              <ChipWrapper>
                <CalendarOutlined /> <span>Active Bookings</span>
              </ChipWrapper>
              {facilityData?.activeBookingsCount || 0}
            </TitleAndCountWrapper>
            <TitleAndCountWrapper>
              <ChipWrapper>
                <TrophyOutlined />
                <span>Sports Types</span>
              </ChipWrapper>
              {facilityData?.sports?.length || 0}
            </TitleAndCountWrapper>
          </TitleAndValueWrapper>
        </Card>
      </CardsWrapper>
      <CardsWrapper span={18}>
        <Card>
          <Title>Contact Information</Title>
          <Row gutter={[6, 6]}>
            {contactInfo?.map((item, index) => (
              <Col key={index} span={6}>
                <ContactInfoWrapper>
                  {item?.icon}
                  <Title>{item?.title}</Title>
                </ContactInfoWrapper>
                <SubText>{item?.value}</SubText>
              </Col>
            ))}
          </Row>
        </Card>
        <Card>
          <Title>Additional Information</Title>
          <TitleAndValueWrapper>
            <Title>Operating Hours</Title>
            <SubText>
              {moment(facilityData?.openingTime, 'HH:mm').format('hh:mm A')} -{' '}
              {moment(facilityData?.closingTime, 'HH:mm').format('hh:mm A')}
            </SubText>
          </TitleAndValueWrapper>
          <TitleAndValueWrapper>
            <Title>Amenities</Title>
            <ChipWrapper>
              {facilityData?.facilityAmenities?.length === 0 ? (
                <SubText>
                  No amenities have been added to this facility yet.
                </SubText>
              ) : (
                facilityData?.facilityAmenities?.map((item, index) => (
                  <Chip key={index}>{AmenityEnum[item]}</Chip>
                ))
              )}
            </ChipWrapper>
          </TitleAndValueWrapper>
        </Card>
      </CardsWrapper>
    </Row>
  );
};

const IconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${COLORS.grey.secondaryLight};
  padding: 4px;
  border-radius: 50px;
`;

const CardsWrapper = styled(Col)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Card = styled.div`
  border: 1px solid ${COLORS.grey.secondary};
  border-radius: 16px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Title = styled.span`
  font-weight: ${FONT_WEIGHTS.BOLD};
`;

const ContactInfoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const SubText = styled.span`
  font-size: ${TEXT_SIZES.XS}px;
`;

const Chip = styled.span`
  background-color: ${COLORS.grey.primary};
  color: ${COLORS.white.primary};
  border-radius: 32px;
  padding: 5px 16px;
  font-size: ${TEXT_SIZES.XS}px;
`;

const ChipWrapper = styled.div`
  display: flex;
  gap: 10px;
`;

const TitleAndValueWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const GrayText = styled.span`
  line-height: 1.3;
`;

const OverviewWrapper = styled.div`
  border: 1px solid ${COLORS.grey.secondary};
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const DescriptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;
`;

const TitleAndCountWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledCarousel = styled(Carousel)`
  .slick-prev,
  .slick-next {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: ${COLORS.black.primary};
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
    cursor: pointer;
    transition: all 0.3s ease;
    color: ${COLORS.white.primary};
  }

  .slick-prev:before,
  .slick-next:before {
    display: none;
  }

  .slick-prev:hover,
  .slick-next:hover {
    background: ${COLORS.skyBlue.primary};
    transform: scale(1.1);
  }

  .slick-prev {
    left: 10px;
  }

  .slick-next {
    right: 10px;
  }
`;

const CarouselWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 250px;
`;

const ArrowButton = styled.div<{ position: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${({ position }) => (position === 'left' ? 'left: 10px;' : 'right: 10px;')}
  transform: translateY(-50%);
  background: ${COLORS.black.primary};
  border-radius: 50%;
  width: 40px;
  height: 40px;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${COLORS.skyBlue.primary};
    transform: translateY(-50%) scale(1.1);
  }
`;

const NoImagesWrapper = styled.div`
  width: 100%;
  height: 250px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLORS.grey.medium};
`;

const Image = styled.img`
  width: 100%;
  height: 250px;
  object-fit: cover;
`;

export default OverviewSection;
